import { useState } from 'react';

import { AssetDTO, EntityRefUtil, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import CheckIcon from '@/components/icons/check';
import QrCodeIcon from '@/components/icons/qr-code';
import { AsyncSelect } from '@/components/ui/async-select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toaster';
import { getAssetsQueryOptions } from '@/queries/assets';
import { awaQrCodeKeys } from '@/queries/awa';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export function AWAAssignQRPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { qrId, workspaceId } = useParams();

  const queryClient = useQueryClient();

  const [selectedAsset, setSelectedAsset] = useState<AssetDTO | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const { data: assets, isLoading: isLoadingAssets } = useQuery({
    ...getAssetsQueryOptions({
      isAWA: true,
      filters: {
        state: RecordState.Active,
      },
    }),
    enabled: !!workspaceId,
  });

  const { mutateAsync: assignQR } = useMutation({
    mutationFn: async ({ assetId }: { assetId: string }) => {
      if (!qrId) throw new Error('QR ID is required');
      await AWAApiV2Service.AssignQRCode({
        assetId: assetId,
        value: qrId,
      });
    },
    onError: (err) => {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    },
  });

  const handleAssign = async () => {
    if (!selectedAsset || !selectedAsset.orgId) return;

    setIsAssigning(true);
    setShowConfirmDialog(false);

    try {
      await assignQR({ assetId: selectedAsset.id });
      await queryClient.invalidateQueries({ queryKey: awaQrCodeKeys.all });
      navigate(`/${selectedAsset.orgId}/assets/${selectedAsset.id}`);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="container mx-auto max-w-2xl space-y-4 rounded-lg bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 font-semibold">
        <QrCodeIcon className="h-5 w-5" />
        <span>QR code not assigned</span>
      </div>

      <div className="space-y-2">
        <p>The QR code you scanned has not yet been assigned to an asset.</p>
        <p>
          Please select an asset from the list below, then click <span className="font-semibold">Assign QR</span>.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setShowConfirmDialog(true);
        }}
        className="space-y-4">
        <AsyncSelect<AssetDTO>
          isDisabled={isLoadingAssets || isAssigning}
          isLoading={isLoadingAssets}
          placeholder="Search asset ID"
          classNamePrefix="react-select"
          value={selectedAsset}
          onChange={(value) => setSelectedAsset(value)}
          defaultOptions={assets ?? []}
          loadOptions={async (input: string) => {
            const { data } = await (
              await AWAApiV2Service.GetAssets({
                limit: 100,
                filters: {
                  state: RecordState.Active,
                  search: input,
                },
              })
            ).json();
            if (!data) return [];
            return data;
          }}
          formatOptionLabel={(option) => EntityRefUtil.createAssetLabel(option)}
        />

        <Button type="submit" disabled={!selectedAsset || isLoadingAssets || isAssigning} className="w-full">
          {isAssigning && <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
          Assign QR
        </Button>
      </form>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign QR Code</DialogTitle>
            <DialogDescription>
              <p>Are you sure you wish to assign this QR sticker to:</p>
              <p className="text-lg font-semibold">{selectedAsset ? EntityRefUtil.createAssetLabel(selectedAsset) : '...'}?</p>
              <div className="mt-4">
                <span className="mr-2 font-semibold">NOTE:</span>
                {selectedAsset?.qrCodes?.length ? (
                  <span>
                    This asset already has QR codes assigned to it. Assigning a new QR code will replace any previously assigned QR codes. Old QR codes for this
                    asset can no longer be used once the procedure is complete.
                  </span>
                ) : (
                  <span>Once this QR code sticker has been assigned to this asset, it cannot be assigned to any other asset.</span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign}>
              <CheckIcon className="mr-2 h-4 w-4" />
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
